import { useRouter } from 'next/router'
import { FormEvent, useEffect, useRef, MouseEvent } from 'react'

// Constants
import { LIVE_MALL_SEARCH_CATEGORY_MAP } from '@/components/pages/livemallclub/Explore/components/Preferences/components/SearchResults/utils/constants'
import { BRIDGE_HANDLER } from '@/constants/bridgeHandler'
import { ROUTE_PATH } from '@/constants/routePath'

// Libs
import { useCategoryList } from '@/components/pages/livemallclub/Explore/components/Preferences/components/SearchResults/hooks/useCategoryList'
import useLiveMallSearchQuery from './useLiveMallSearchQuery'
import { execute } from '@tutorabc/webview-jsbridge'

export const useLiveMallSearch = () => {
  const router = useRouter()
  const { category: searchParamCategory, keyword: searchParamKeyword } = router.query
  const SEARCH_CATEGORIES = useCategoryList()
  const inputRef = useRef<HTMLInputElement>(null)
  const { simpleRefetch } = useLiveMallSearchQuery({
    keyword: String(searchParamKeyword || ''),
    type: LIVE_MALL_SEARCH_CATEGORY_MAP[String(searchParamCategory).toUpperCase() as keyof typeof LIVE_MALL_SEARCH_CATEGORY_MAP],
    enabled: !!searchParamCategory,
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement> | MouseEvent) => {
    event.preventDefault()
    const inputValue = inputRef.current?.value
    if (!inputValue) return

    const isSameKeyword = inputValue === String(searchParamKeyword)
    if (isSameKeyword && searchParamCategory) {
      simpleRefetch()
      return
    }

    if (router.pathname === ROUTE_PATH.EXPLORE) {
      execute({
        handlerName: BRIDGE_HANDLER.SEARCH,
        data: {
          keyword: inputValue,
          category: 'all',
        },
      })
    } else {
      router.push({ pathname: ROUTE_PATH.SEARCH, query: { keyword: inputValue, category: searchParamCategory ?? SEARCH_CATEGORIES[0].value } })
    }
  }

  const clearInput = () => {
    if (!inputRef.current) return
    inputRef.current.value = ''
    inputRef.current.focus()
    router.push({ pathname: ROUTE_PATH.SEARCH, query: { keyword: '', category: searchParamCategory ?? SEARCH_CATEGORIES[0].value } })
  }

  useEffect(() => {
    if (inputRef.current && searchParamKeyword && searchParamKeyword !== inputRef.current.value) {
      inputRef.current.value = String(searchParamKeyword)
    }
  }, [searchParamKeyword, searchParamCategory])

  return {
    handleSubmit,
    inputRef,
    clearInput,
  }
}

export default useLiveMallSearch
